(async() => {
    const { Core } = await importModule('Core');
    const { $JSON } = await importModule('Utils');

    /**
     * CUSTOM VARIANT SELECTOR — Legacy Availability Logic
     * =====================================================
     * This component replicates the old theme's variant selection behaviour:
     *   1. On page load: hide all option values that have no available stock.
     *   2. On option click: auto-select a valid fallback if the chosen combination
     *      doesn't exist, rather than showing "Unavailable".
     *   3. Only after the final variant is resolved: notify the rest of the theme
     *      (price, add-to-cart button, gallery) with the confirmed variant ID.
     *
     * KEY INTEGRATION POINT — product-variant-provider.js
     * The new theme's provider was fetching section HTML from Shopify using
     * ?option_values=... which could return "Unavailable" for ambiguous combos.
     * We patch _fetchDoc() to use ?variant=ID (matching the old theme's approach)
     * whenever a resolved ID is available. See product-variant-provider.js _fetchDoc().
     */
    customElements.define('product-variant-selector', class extends Core {

        render() {
            this.addEventListener('change', this._onVariantChange.bind(this));

            // JSON data is embedded inside this element by product-variant-selector.liquid
            // (inside <script type="application/json"> tags) so it is scoped to this component.
            const variantsEl = this.querySelector('[data-variants-json]');
            const optionsEl  = this.querySelector('[data-options-json]');

            if (variantsEl && optionsEl) {
                this.optionsMap = {};
                this.options  = $JSON(optionsEl);
                this.variants = $JSON(variantsEl);

                // Read the initial variant from the 'variant-id' attribute set on this element
                // by Liquid (product.selected_or_first_available_variant.id).
                // We cannot query input[name="id"] because that input lives in a sibling
                // <product-variant-listener>, not inside this element.
                const currentVariantId = parseInt(this.getAttribute('variant-id'));
                this.variant = this.variants.find(({ id }) => id === currentVariantId) || this.variants[0];

                // Build the availability map and hide unavailable options on initial load.
                this._linkOptionSelectors();
                this._checkUnavailable();
            }
        }

        /**
         * Called whenever any radio input inside this element fires a 'change' event.
         *
         * RECURSION GUARD
         * When this handler silently updates other radio buttons to reflect the fallback
         * combination (see below), those updates also fire 'change' events. We dispatch
         * them as CustomEvent so this guard catches them and returns immediately, preventing
         * an infinite cascade of publish() calls to the provider.
         */
        _onVariantChange(e) {
            this._updateDisplayValue(e.target);

            // Stop processing silently-dispatched fallback events (CustomEvent).
            // These are internal corrections — no publish needed.
            if (e instanceof CustomEvent) return;

            if (!this.variants || !this.options) return;

            // Collect the current checked value for each option group.
            let selectedOptions = [];
            this.querySelectorAll('input[type="radio"]:checked').forEach(input => {
                const index = parseInt(input.dataset.optionIndex);
                if (!isNaN(index)) selectedOptions[index] = input.value;
            });

            const changedIndex = parseInt(e.target.dataset.optionIndex);
            const changedValue = e.target.value;

            // Step 1: Try to find a variant that exactly matches all currently selected values.
            let matchedVariant = this.variants.find(v =>
                v.options.every((opt, i) => opt === selectedOptions[i])
            );

            if (!matchedVariant) {
                // Step 2: No exact match — the selected combination doesn't exist in the catalogue.
                // Replicate the old theme's _getFirstAvailableVariant() behaviour:
                //   - Prefer the first AVAILABLE variant that includes the newly-selected option.
                //   - Fall back to any variant with that option if none are available.
                matchedVariant = this.variants.find(v =>
                    v.options[changedIndex] === changedValue && v.available
                ) || this.variants.find(v => v.options[changedIndex] === changedValue);

                // Step 3: Silently correct the other radio buttons to match the fallback variant.
                // We dispatch CustomEvent (not plain Event) so the recursion guard above stops
                // these from re-entering the full publish pipeline.
                if (matchedVariant) {
                    matchedVariant.options.forEach((optValue, i) => {
                        if (i !== changedIndex) {
                            const input = this._findInputByNameValue(this.options[i].name, optValue);
                            if (input && !input.checked) {
                                input.checked = true;
                                input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: true }));
                            }
                        }
                    });
                }
            }

            if (matchedVariant) {
                this.variant = matchedVariant;
                // Step 4: Update the hidden/shown state of all option labels.
                // _checkUnavailable() may further correct this.variant silently.
                this._checkUnavailable();
            }

            // Step 5: Publish exactly once, with the fully-resolved variant ID.
            // Publishing BEFORE resolving the fallback (as earlier versions did) caused the
            // provider to receive undefined/wrong IDs and fetch "Unavailable" HTML from Shopify.
            this.publish('variant:change', {
                optionValueId: e.target.dataset.optionValueId,
                optionIndex:   e.target.dataset.optionIndex,
                variantId:     this.variant ? this.variant.id : e.target.dataset.variantId,
                imagePosition: this.variant ? this.variant.featured_media?.position : e.target.dataset.imagePosition
            });
        }

        // --- Options map helpers ---------------------------------------------

        _arrayIncludes(arr, val) {
            for (var n = 0; n < arr.length; n++) if (arr[n] == val) return true;
            return false;
        }

        _uniq(arr) {
            var out = [];
            for (var n = 0; n < arr.length; n++) this._arrayIncludes(out, arr[n]) || out.push(arr[n]);
            return out;
        }

        /**
         * Build optionsMap — a dictionary of *available* option combinations.
         * Mirrors the old theme's _linkOptionSelectors().
         *
         *   optionsMap['root']           → all available option1 values
         *   optionsMap['48 in OD']       → option2 values available when option1='48 in OD'
         *   optionsMap['48 in OD / 0.5'] → option3 values available for that combination
         *
         * Only variants where variant.available === true are included, so unavailable
         * combos are naturally excluded from the map and will be hidden.
         */
        _linkOptionSelectors() {
            for (var i = 0; i < this.variants.length; i++) {
                var variant = this.variants[i];
                if (variant.available) {
                    this.optionsMap['root'] = this._uniq((this.optionsMap['root'] || []).concat(variant.option1));

                    if (this.options.length > 1) {
                        var k1 = variant.option1;
                        this.optionsMap[k1] = this._uniq((this.optionsMap[k1] || []).concat(variant.option2));
                    }

                    if (this.options.length === 3) {
                        var k2 = variant.option1 + ' / ' + variant.option2;
                        this.optionsMap[k2] = this._uniq((this.optionsMap[k2] || []).concat(variant.option3));
                    }
                }
            }
        }

        // --- Availability hiding ---------------------------------------------

        /**
         * For each option group, determine which values are valid given the current
         * variant selection and apply/remove [data-soldout] on their <label> elements.
         * The CSS rule `[data-soldout] { display: none !important }` hides them visually.
         *
         * If the currently-selected value has become hidden (because another option changed
         * and its combination no longer exists), silently move the checked state to the
         * first still-visible label and update this.variant accordingly.
         *
         * IMPORTANT: No events are dispatched here. All corrections happen in-place so that
         * _onVariantChange's single final publish() is the only notification the provider
         * ever receives per user interaction.
         */
        _checkUnavailable() {
            this.options.forEach((option, i) => {
                let needed_array;
                const currentOpts = [...this.variant.options];

                // Look up which values are available at this option position given
                // the already-selected values at earlier positions.
                if (i === 0) {
                    needed_array = this.optionsMap['root'];
                } else if (i === 1) {
                    needed_array = this.optionsMap[currentOpts[0]];
                } else if (i === 2) {
                    needed_array = this.optionsMap[currentOpts[0] + ' / ' + currentOpts[1]];
                }

                option.values.forEach(optionValue => {
                    const isValid = Array.isArray(needed_array) && needed_array.indexOf(optionValue) !== -1;
                    // Set/remove [data-soldout] on the <label>; CSS hides it when present.
                    this._toggleOptionHide(option.name, optionValue, !isValid);
                });
            });

            // Check whether any currently-selected option has just been hidden.
            let changed = false;
            this.variant.options.forEach((optionValue) => {
                const input = this._findInput(optionValue);
                if (!input) return;
                const label = input.closest('label');
                if (label && label.hasAttribute('data-soldout')) {
                    // The selected value is now hidden — silently move to the first visible sibling.
                    const firstVisible = label.closest('div').querySelector('label:not([data-soldout])');
                    if (firstVisible) {
                        const firstInput = firstVisible.querySelector('input');
                        if (firstInput && !firstInput.checked) {
                            firstInput.checked = true;
                            this._updateDisplayValue(firstInput);
                            changed = true; // Mark that we updated the DOM
                        }
                    }
                }
            });

            // If we silently moved any checked state, recompute this.variant so that
            // the final publish() in _onVariantChange sends the correct ID.
            if (changed) {
                let newSelected = [];
                this.querySelectorAll('input[type="radio"]:checked').forEach(inp => {
                    const idx = parseInt(inp.dataset.optionIndex);
                    if (!isNaN(idx)) newSelected[idx] = inp.value;
                });
                const corrected = this.variants.find(v =>
                    v.options.every((opt, i) => opt === newSelected[i])
                );
                if (corrected) this.variant = corrected;
            }
        }

        /**
         * Toggle the [data-soldout] attribute on the <label> wrapping the input.
         *
         * Old theme used closest('[data-swatch-field]') — the new theme's labels do NOT
         * carry that attribute, so we use closest('label') instead.
         * The CSS hiding rule works identically in both cases.
         */
        _toggleOptionHide(name, value, disable) {
            const input = this._findInputByNameValue(name, value);
            if (!input) return;
            const label = input.closest('label');
            if (!label) return;
            if (disable) {
                label.setAttribute('data-soldout', '');
            } else {
                label.removeAttribute('data-soldout');
                // When an item becomes available again after an option change, clear any
                // !unavailable / !sold-out classes that were baked in at Liquid render time
                // for the initial selection. Without this, visible dropdown items would
                // still carry stale "unavailable" styling (opacity + text) even though
                // they are now selectable.
                input.classList.remove('!unavailable', '!sold-out');
            }
        }

        // --- DOM helpers -----------------------------------------------------

        _findInput(value) {
            const escapedValue = value.replace(/["\\]/g, '\\$&');
            return this.querySelector(`input[value="${escapedValue}"]`);
        }

        _findInputByNameValue(name, value) {
            const escapedName = name.replace(/["\\]/g, '\\$&');
            const escapedValue = value.replace(/["\\]/g, '\\$&');
            return this.querySelector(`input[name="${escapedName}"][value="${escapedValue}"]`);
        }

        _updateDisplayValue(target) {
            const displayValues = Array.from(this.querySelectorAll('*[data-display-value]'));
            if(!displayValues.length) return;
            const match = displayValues.find(item => item.getAttribute('data-option-index') === target.getAttribute('data-option-index'));
            if(match) match.innerText = target.value;
        }
    });
})()




// Default theme code

// (async() => {
//     const { Core } = await importModule('Core');
//     customElements.define('product-variant-selector', class extends Core {
        
//         render() {
//             this.addEventListener('change', this._onVariantChange.bind(this));
//         }
        
//         _onVariantChange(e) {            
//             this.publish('variant:change', {
//                 optionValueId: e.target.dataset.optionValueId, 
//                 optionIndex: e.target.dataset.optionIndex,
//                 variantId: e.target.dataset.variantId,
//                 imagePosition: e.target.dataset.imagePosition
//             });
//         }
//     });
// })()